import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../shared/ss-select-tooltip.directive';

import type { LabelValueOption } from '../shared/self-service-config.types';
import { DvmBuilderHost } from './dvm-builder-host';
import type { DashboardTile, TargetFormulaReferenceEntry } from './dvm-layout-builder.types';

/** One target of a tile after editing its interactions: the formulas are only present for INPUT tiles. */
export interface TileInteractionTarget {
  componentId: string;
  label: string;
  formulas?: Array<Record<string, unknown>>;
}

export interface TileInteractionsSave {
  tile: DashboardTile;
  isInput: boolean;
  targets: TileInteractionTarget[];
}

/**
 * The "Interactions" overlay of a dashboard tile: which live tiles it drives
 * (any tile) and, for INPUT controls, the per-target formula + typed
 * references. It keeps its OWN draft state so the builder form on the left
 * is untouched, and hands the result back through `save`.
 */
@Component({
  selector: 'app-tile-interactions-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, NgSelectModule, SsSelectTooltipDirective],
  templateUrl: './tile-interactions-modal.component.html',
  styles: [':host { display: contents; }'],
})
export class TileInteractionsModalComponent implements OnChanges {
  readonly host = inject(DvmBuilderHost);

  /** The tile being edited; setting it (re)seeds the draft. */
  @Input({ required: true }) tile!: DashboardTile;
  @Output() readonly save = new EventEmitter<TileInteractionsSave>();
  @Output() readonly cancel = new EventEmitter<void>();

  targets: string[] = [];
  formulas: Record<string, string> = {};
  stepFormulas: Record<string, Record<string, string>> = {};
  references: Record<string, TargetFormulaReferenceEntry[]> = {};
  targetOptions: LabelValueOption[] = [];

  /** Only input controls push a computed value, so only they get per-target formulas. */
  get isInput(): boolean {
    const layoutType = this.tile?.layoutType;
    return layoutType === 'SLIDER' || layoutType === 'INPUT_BOX' || layoutType === 'DROPDOWN';
  }

  ngOnChanges(): void {
    if (!this.tile) return;
    this.seedFromTile(this.tile);
    this.targetOptions = this.buildTargetOptions(this.tile);
  }

  stepFormula(id: string, step: string): string {
    return this.stepFormulas[id]?.[step] ?? '';
  }
  setStepFormula(id: string, step: string, value: string): void {
    const perStep = { ...(this.stepFormulas[id] ?? {}) };
    perStep[step] = value ?? '';
    this.stepFormulas = { ...this.stepFormulas, [id]: perStep };
  }
  refs(id: string, step?: string): TargetFormulaReferenceEntry[] {
    return (this.references[id] || []).filter((entry) => (step ? entry.step === step : !entry.step));
  }
  addRef(id: string, step?: string): void {
    const rows = [...(this.references[id] || []), { token: '', inputTitle: '', targetLabel: '', rowLabel: '', step }];
    this.references = { ...this.references, [id]: rows };
  }
  removeRef(id: string, entry: TargetFormulaReferenceEntry): void {
    const rows = (this.references[id] || []).filter((item) => item !== entry);
    this.references = { ...this.references, [id]: rows };
  }

  onSave(): void {
    const isInput = this.isInput;
    const targets: TileInteractionTarget[] = this.targets.map((id) => ({
      componentId: id,
      label: this.host.targetLabelForId(id),
      ...(isInput ? { formulas: this.formulasForPayload(id) } : {}),
    }));
    this.save.emit({ tile: this.tile, isInput, targets });
  }

  onCancel(): void {
    this.cancel.emit();
  }

  // ── internals ───────────────────────────────────────────────────────────

  /** Every other tile except popups; inputs are marked so the picker reads clearly. */
  private buildTargetOptions(tile: DashboardTile): LabelValueOption[] {
    const selfId = String(tile.componentId ?? '');
    const seen = new Set<string>();
    const options: LabelValueOption[] = [];
    this.host.dashboardTiles.forEach((item) => {
      const value = String(item.componentId ?? '');
      if (!value || value === selfId || seen.has(value) || item.layoutType === 'POPUP') return;
      seen.add(value);
      const isInput = item.layoutType === 'SLIDER' || item.layoutType === 'INPUT_BOX' || item.layoutType === 'DROPDOWN' || item.layoutType === 'FILE_UPLOAD';
      options.push({ label: (item.title || item.layoutType) + (isInput ? ' (Input)' : ''), value });
    });
    return options;
  }

  /** Draft state from the tile's saved `payload.targets` (current `formulas[]` shape or the legacy flat `formula`). */
  private seedFromTile(tile: DashboardTile): void {
    this.targets = [];
    this.formulas = {};
    this.stepFormulas = {};
    this.references = {};
    const targets = Array.isArray(tile.payload?.['targets']) ? tile.payload!['targets'] as Array<Record<string, unknown>> : [];
    targets.forEach((target) => {
      const id = String(target?.['componentId'] ?? '').trim();
      if (!id || this.targets.includes(id)) return;
      this.targets.push(id);
      const entries = Array.isArray(target['formulas']) ? target['formulas'] as Array<Record<string, unknown>> : null;
      if (entries) {
        const withStep = entries.filter((e) => !!e['step']);
        if (withStep.length) {
          const perStep: Record<string, string> = {};
          withStep.forEach((e) => { perStep[String(e['step'])] = String(e['formula'] ?? ''); });
          this.stepFormulas[id] = perStep;
        } else {
          this.formulas[id] = String(entries[0]?.['formula'] ?? '');
        }
        const rows: TargetFormulaReferenceEntry[] = [];
        entries.forEach((e) => {
          const refs = e['references'] as Record<string, Record<string, unknown>> | undefined;
          if (!refs || typeof refs !== 'object') return;
          const entryStep = e['step'] ? String(e['step']) : undefined;
          Object.keys(refs).forEach((token) => {
            const r = refs[token] || {};
            rows.push({ token, inputTitle: String(r['inputTitle'] ?? ''), targetLabel: String(r['targetLabel'] ?? ''), rowLabel: String(r['rowLabel'] ?? ''), step: entryStep });
          });
        });
        if (rows.length) this.references[id] = rows;
      } else {
        this.formulas[id] = String(target['formula'] ?? '');
      }
    });
  }

  private formulasForPayload(id: string): Array<Record<string, unknown>> {
    const refsFor = (step?: string): Record<string, unknown> | undefined => {
      const entries = (this.references[id] || []).filter((e) => (step ? e.step === step : !e.step));
      const map: Record<string, unknown> = {};
      entries.forEach((e) => {
        const token = e.token.trim();
        if (!token) return;
        map[token] = { inputTitle: e.inputTitle.trim(), targetLabel: e.targetLabel.trim(), rowLabel: e.rowLabel.trim() };
      });
      return Object.keys(map).length ? map : undefined;
    };
    if (this.host.isWaterfallTarget(id)) {
      return this.host.targetSteps(id)
        .map((step) => {
          const formula = this.stepFormula(id, step.label).trim();
          const references = refsFor(step.label);
          return { step: step.label, formula, ...(references ? { references } : {}) };
        })
        .filter((entry) => !!entry['formula']);
    }
    const formula = (this.formulas[id] || '').trim();
    if (!formula) return this.host.isInputControlTargetId(id) ? [{ formula: 'value' }] : [];
    const references = refsFor();
    return [{ formula, ...(references ? { references } : {}) }];
  }
}
